import axios from 'axios';
import fileDownload from 'js-file-download';
import _ from 'lodash';
import { DateTime, Duration } from 'luxon';
import { RRule, RRuleSet } from 'rrule';

import { TRIP_STATUS_CODES, TRIP_TYPE_CODES } from '@/constants';
import { LOCATION_CATEGORIES } from '@/features/trip-creation/components/TripDetailsStep/components/RouteAndSchedule/components/LocationInput/constants';
import {
  isHiddenPermission,
  PERMISSION_LABELS,
  permissionKey,
} from 'components/v2/Settings/components/AddEditRoleModal/constants';
import {
  BOOKING_TYPE_VALUES,
  CONTRACT_STATUS_CODES,
  FREQUENCY_KEY_MAP,
  MEAN_EARTH_RADIUS,
  PARENT_DOCUMENT_COLLECTION,
  ROUND_MILES_VALUES,
  SECONDS_PER_HOUR,
  TNC_PROVIDERS,
  TRIP_STATUS_CODE_STYLES,
  VALID_RE_ASSIGNABLE_STATUS_LIST,
} from 'utils/constants';

/**
 * Function takes in formatted_address and geometry from google geocode response and returns location object
 *
 * @param {*} address formatted_address string from google
 * @param {*} geometry object containing lat and long
 * @param placeID
 * @param types
 * @param clearFetchLocationLoadingState clears the loading state if the address is not in correct format.
 * @returns
 */
export const formatGeocode = (
  address,
  geometry,
  placeID,
  types = [],
  clearFetchLocationLoadingState = () => {},
) => {
  const splitAddress = address.split(', ');
  if (splitAddress.length < 4) {
    clearFetchLocationLoadingState?.();
    throw Error('Invalid or incomplete address, please try again.');
  } else {
    const [addressOne, city, stateZip, country] = splitAddress.slice(
      splitAddress.length - 4,
      splitAddress.length,
    );

    const stateZipSplit = stateZip.split(' ');
    const zip = stateZipSplit[stateZipSplit.length - 1];
    const state = stateZipSplit.slice(0, stateZipSplit.length - 1).join(' ');
    const latitude = geometry.location.lat;
    const longitude = geometry.location.lng;

    if (
      !addressOne ||
      !placeID ||
      !types ||
      !city ||
      !state ||
      !zip ||
      !country
    ) {
      clearFetchLocationLoadingState?.();
      throw Error('Invalid or incomplete address, please try again.');
    }

    return {
      addressOne,
      placeID,
      types,
      city,
      state,
      zip: zip?.slice(0, 5), // trim long zipcodes
      country,
      longLat: {
        type: 'Point',
        // longitude must always be first for mongo geo json
        coordinates: [longitude, latitude],
      },
      formattedAddress: address,
    };
  }
};

export const getDurationFromDistanceMatrix = (rows) =>
  rows.length && rows[0].elements.length
    ? rows[0].elements[0].duration.value
    : null;

export const ab2str = (buf) => {
  const decoder = new TextDecoder('utf-8');
  return decoder.decode(new Uint8Array(buf));
};

export function removeEmptyKeys(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([, v]) => v !== null || (Array.isArray(v) && v.length > 0),
    ),
  );
}

export function removeUndefinedKeys(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([, v]) => v !== undefined || (Array.isArray(v) && v.length > 0),
    ),
  );
}

/**
 * Removes keys with empty values.
 * @param {Object} object input object
 * @param {String[]} excludedKeys the keys which is not removed even if it is empty.
 * @return {Object} resulting object.
 */
export function removeNestedEmptyKeys(object, excludedKeys = []) {
  Object.entries(object).forEach(([k, v]) => {
    if (!excludedKeys.includes(k)) {
      if (v && typeof v === 'object') {
        removeNestedEmptyKeys(v, excludedKeys);
        // eslint-disable-next-line no-param-reassign
        if (Object.keys(v).length === 0) delete object[k];
      } else if (
        v === null ||
        v === undefined ||
        v === '' ||
        (Array.isArray(v) && v.length === 0) ||
        v.length === 0
      ) {
        if (Array.isArray(object)) {
          object.splice(k, 1);
        } else if (!(v instanceof Date)) {
          /* eslint-disable*/
          delete object[k];
        }
      }
    }
  });
  return object;
}

/**
 * Removes keys with empty strings.
 * @param {Object} object input object
 * @return {Object} resulting object.
 */
export function removeEmptyStrings(object) {
  Object.entries(object).forEach(([k, v]) => {
    if (v === '') {
      delete object[k];
    }
  });
  return object;
}

/**
 * Creates a filter function to check if an item matches any of the specified options.
 *
 * @param {Array<null|undefined|string>} [opts=[null, undefined, "", '[]', '{}']] - Array of options to filter out. Default includes null, undefined, empty string, empty array, and empty object.
 *
 * @returns {Function} - Returns a filter function that checks if an item matches any of the specified options.
 */
export const getFilterBuilder = (opts = [null, undefined, '', '[]', '{}']) => {
  const func = (item) => {
    const matched = opts.some((opt) => {
      switch (opt) {
        case null:
        case undefined:
        case '':
          if (item === opt) return true;
          break;
        case '[]':
          if (typeof item === 'object' && item.length === 0) return true;
          break;
        case '{}':
          if (typeof item === 'object' && _.isEmpty(item)) return true;
          break;
        default:
          break;
      }
    });
    return matched;
  };
  return func;
};

/**
 * Sanitizes form data by removing specified unwanted values and empty objects/arrays.
 *
 * @param {Object|Array} obj - The form data to sanitize.
 * @param {Function} [customFilter=getFilterBuilder()] - The filter function to determine unwanted values.
 * @param {Array<string>} [excludedKeys=[]] - List of keys to exclude from sanitization.
 * @param {Array<string>} [skipNestedKeys=[]] - List of keys whose nested values should not be sanitized.
 *
 * @returns {Object|Array} - Returns the sanitized form data.
 */
export const sanitizeFormData = (
  obj,
  customFilter = getFilterBuilder(),
  excludedKeys = [],
  skipNestedKeys = [],
) => {
  try {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        if (typeof item === 'object') {
          obj[index] = sanitizeFormData(
            item,
            customFilter,
            excludedKeys,
            skipNestedKeys,
          );
        }
      });
      const filteredValues = obj.filter((item) => !customFilter(item));
      return filteredValues.map((value) =>
        typeof value === 'string' ? value.trim() : value,
      );
    }

    Object.keys(obj).forEach((key) => {
      if (
        typeof obj[key] === 'object' &&
        !excludedKeys.includes(key) &&
        !skipNestedKeys.includes(key)
      ) {
        obj[key] = sanitizeFormData(
          obj[key],
          customFilter,
          excludedKeys,
          skipNestedKeys,
        );
      }
    });

    Object.keys(obj).forEach((key) => {
      if (!excludedKeys.includes(key)) {
        if (customFilter(obj[key])) {
          delete obj[key];
        } else if (typeof obj[key] === 'string') {
          obj[key] = obj[key].trim();
        }
      }
    });
    return obj;
  } catch {
    return obj;
  }
};

export const formatDateTime = (datetime, format) => {
  let formattedDateTime;
  if (datetime instanceof Date)
    formattedDateTime = DateTime.fromJSDate(datetime);
  else formattedDateTime = DateTime.fromISO(datetime);

  if (formattedDateTime?.isValid)
    return formattedDateTime.toFormat(format, { locale: 'en-US' });
  // Invalid/missing input must not be rendered as the current date/time — that
  // produces a wrong-but-plausible value on trip tables, activity logs and
  // printed PDFs (TS-9342). Return empty string, matching zoneIgnoredDate below.
  return '';
};

export const zoneIgnoredDate = (
  inputStringOrLuxon,
  returnNullForInvalidDate = false,
) => {
  if (!inputStringOrLuxon) {
    if (returnNullForInvalidDate) return null;
    else return '';
  }
  if (typeof inputStringOrLuxon === 'string') {
    return DateTime.fromISO(inputStringOrLuxon.slice(0, 10), {
      zone: 'utc',
    }).startOf('day');
  }
  if (inputStringOrLuxon instanceof DateTime) {
    if (!inputStringOrLuxon.isValid) return inputStringOrLuxon;
    return DateTime.fromISO(inputStringOrLuxon.toISO().slice(0, 10), {
      zone: 'utc',
    }).startOf('day');
  }
  return '';
};

export const formatIcdResponse = (response) => {
  const mainData = response[3];
  return mainData.map((data) => ({
    displayText: data[1],
    icd10Code: data[0],
  }));
};

export const renderDropoffTime = (
  actualDropoffDateTime,
  requestedDropoffDateTime,
  estimatedDropoffDateTime,
) => {
  if (actualDropoffDateTime)
    return formatDateTime(actualDropoffDateTime, 'hh:mm a');
  if (requestedDropoffDateTime)
    return formatDateTime(requestedDropoffDateTime, 'hh:mm a');
  if (estimatedDropoffDateTime)
    return formatDateTime(estimatedDropoffDateTime, 'hh:mm a');
  return 'Pending';
};

export const downloadFileFromUrl = (downloadUrl, filename) => {
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.dispatchEvent(
    new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    }),
  );
  document.body.removeChild(link);
};

export const handleDownload = (url, filename) => {
  axios
    .get(url, {
      responseType: 'blob',
    })
    .then((res) => {
      fileDownload(res.data, filename);
    })
    .catch((err) => {
      console.error('Download failed:', err);
    });
};

// Cut string in "text svg"
export const trimText = (text, threshold) =>
  text.length <= threshold ? text : text.substr(0, threshold).concat('...');

/* eslint-disable */
/**
 * Function works with SearchBox and Strictly for member search
 *
 * @param {*} searchResults list of member from member search API
 * @param {*} inputValue User input string
 * @returns
 */
export const filterMemberOptions = (
  searchResults,
  { inputValue = '' },
  seeAllResults,
) => {
  if (inputValue.length) {
    const filteredResults = searchResults.filter((option) =>
      inputValue.startsWith('!')
        ? zoneIgnoredDate(option.dateOfBirth).toFormat('MM/dd/yyyy') ===
          inputValue.trim().substring(1).toLowerCase()
        : inputValue.startsWith('@')
          ? `${option.name.firstName} ${option.name.lastName}`
              .toLowerCase()
              .indexOf(inputValue.trim().substring(1).toLowerCase()) !== -1
          : true,
    );
    if (
      inputValue.startsWith('@') &&
      filteredResults.length > 10 &&
      !seeAllResults
    ) {
      return [...filteredResults.slice(0, 10), { more: true }]; // TODO: Do not fetch if we don't need. use page and limit
    }
    return filteredResults;
  }
  return [{ hint: true }]; // If input value is missing, we need to show search hint
};

/**
 * Function for making underscore seperated word into space seperated capitalized sentence
 *
 * @param {*} underScoreName list of member from member search API
 * @returns
 */
export const wordCapitalize = (underScoreName) =>
  underScoreName
    .split('_')
    .map((word) => word[0].toUpperCase() + word.substring(1))
    .join(' ');

/**
 * TS-9454: display names come from the curated map the Add Role modal renders,
 * so a permission is named identically wherever it appears. A key the map does
 * not cover (a scope the backend ships ahead of the modal) still renders, via
 * the original capitalize-the-raw-key fallback. Permissions hidden from the
 * UI (TS-9863) get no text at all.
 */
export const getPermissionTexts = (permissions) =>
  Object.keys(permissions)
    .map((section) => {
      const shown = permissions[section].filter(
        (word) => !isHiddenPermission(section, word),
      );
      return (
        (shown.length &&
          shown.map(
            (word) =>
              PERMISSION_LABELS[permissionKey(section, word)] ||
              `${wordCapitalize(section)} ${wordCapitalize(word)}`,
          )) ||
        null
      );
    })
    .filter((item) => item !== null);

/* eslint-enable */

export const getLogTextFromEvent = (
  event,
  eventLogMap,
  originalObject = {},
  updatedByName = '',
) => {
  const mapEventSourceType = {
    trips: 'Created Trip',
    rates: 'Added New Rate',
    contracts: 'Created Contract',
  };

  const handleInsertEvent = (originalObjectType) => {
    const { createdBy = {} } = originalObject;
    return `${createdBy?.name || 'Someone'} ${
      mapEventSourceType[originalObjectType] || 'Instance'
    }`;
  };

  const handleUpdateEvent = (
    updatedFields,
    eventMessageMap,
    previousValues,
    parentDocumentCollection,
  ) => {
    const updatedFieldsArray = Object.keys(updatedFields);
    const pathHasBeenChecked = {};
    const eventMessageArray = updatedFieldsArray.map((updatedField) => {
      const updatedFieldPathKeys = updatedField.split('.');
      return mapUpdatedField(
        updatedFieldPathKeys,
        eventMessageMap,
        pathHasBeenChecked,
      );
    });
    let updateLog = eventMessageArray.filter(Boolean).join(', ');
    const updatedBy = updatedFields['updatedBy.name'] || updatedByName;
    let message = '';
    let addedUpdatedBy = false;
    if (
      Object.hasOwn(updatedFields, 'status') &&
      Object.hasOwn(previousValues ?? {}, 'status')
    ) {
      const previousStatus = previousValues?.status ?? '';
      const currentStatus = updatedFields?.status;
      const getUpdateStatusMessage = (fromStatus, toStatus) =>
        `Updated status from "${fromStatus}" to "${toStatus}"`;

      switch (parentDocumentCollection) {
        case PARENT_DOCUMENT_COLLECTION.TRIPS:
          if (
            currentStatus === TRIP_STATUS_CODES.APPROVAL_DENIED ||
            currentStatus === TRIP_STATUS_CODES.DENIED
          ) {
            message = `Denied by ${updatedBy}`;
            addedUpdatedBy = true;
          } else if (
            previousStatus === TRIP_STATUS_CODES.APPROVAL_PENDING &&
            currentStatus !== TRIP_STATUS_CODES.APPROVAL_DENIED &&
            currentStatus !== TRIP_STATUS_CODES.REQUESTER_CANCELLED
          ) {
            message = `Approved by ${updatedBy}`;
            addedUpdatedBy = true;
          } else if (
            previousStatus === TRIP_STATUS_CODES.ASSIGNMENT_PENDING &&
            currentStatus === TRIP_STATUS_CODES.ASSIGNMENT_EXPORTED
          ) {
            updateLog = '';
            message = '';
          } else {
            message = getUpdateStatusMessage(
              TRIP_STATUS_CODE_STYLES[previousStatus]?.title,
              TRIP_STATUS_CODE_STYLES[currentStatus]?.title,
            );
            addedUpdatedBy = false;
          }
          break;
        case PARENT_DOCUMENT_COLLECTION.CONTRACTS:
          message = getUpdateStatusMessage(
            CONTRACT_STATUS_CODES[previousStatus],
            CONTRACT_STATUS_CODES[currentStatus],
          );
          break;
        default:
          message = getUpdateStatusMessage(previousStatus, currentStatus);
          break;
      }
    }

    if (message.length > 0) {
      if (addedUpdatedBy) return `${message}`;
      return `${updatedBy} ${message}`;
    }
    if (updateLog) {
      return `${updatedBy} Updated ${updateLog}`;
    }
    return null;
  };

  const mapUpdatedField = (
    updatedFieldPathArray,
    eventMessageMap,
    pathHasBeenChecked,
  ) => {
    if (!updatedFieldPathArray.length) return null;
    const rootFieldKey =
      updatedFieldPathArray[0] === 'appointmentReasons' ||
      updatedFieldPathArray[0] === 'priorAuthorization' ||
      updatedFieldPathArray[0] === 'referringProvider'
        ? 'serviceInfo'
        : updatedFieldPathArray[0];
    const updatedFieldMessage = eventMessageMap[rootFieldKey];
    if (
      pathHasBeenChecked[rootFieldKey] &&
      typeof pathHasBeenChecked[rootFieldKey] !== 'object'
    ) {
      return null;
    }
    if (!updatedFieldMessage) return null;
    if (typeof updatedFieldMessage === 'object') {
      // eslint-disable-next-line no-param-reassign
      pathHasBeenChecked[rootFieldKey] = {};
      return mapUpdatedField(
        updatedFieldPathArray.slice(1),
        updatedFieldMessage,
        pathHasBeenChecked,
      );
    }
    // eslint-disable-next-line no-param-reassign
    pathHasBeenChecked[rootFieldKey] = true;
    return updatedFieldMessage();
  };

  const handleRateDelete = () => {
    const {
      body: {
        updatedFields: { deletedBy },
      },
    } = event;
    return `${deletedBy?.name || 'Someone'} Deleted A Rate`;
  };

  const { body, type, parentDocumentCollection } = event;
  switch (type) {
    case 'insert':
      return handleInsertEvent(parentDocumentCollection);
    case 'update':
      return handleUpdateEvent(
        body?.updatedFields || {},
        eventLogMap,
        body?.previousValues,
        parentDocumentCollection,
      );
    case 'rate-delete':
      return handleRateDelete();
    default:
      return 'Unknown Event Type';
  }
};

export const getCamelCaseFormattedAddress = (address) => {
  if (!address || !address.length) return '';
  return address
    .split(' ')
    .map(
      (word) =>
        (word?.[0]?.toUpperCase() || '') +
        (word?.substring(1)?.toLowerCase() || ''),
    )
    .join(' ');
};

export const isNumeric = (str) => {
  if (typeof str === 'number' && !Number.isNaN(str)) return true;
  if (typeof str !== 'string') return false;

  // RegExp to match valid numeric strings with optional signs, decimal points, and exponents
  const numericRegex = /^(?:-|\+)?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?$/;

  return numericRegex.test(str);
};

export const getCapitalizedStringWithUnderscore = (value) => {
  if (!value || !value.length) return '';
  return value
    .trim()
    .split(' ')
    .map((word) => (word.length ? word.toUpperCase() : ''))
    .join('_');
};

export const getCamelCaseStringFromCapitalizedString = (value) => {
  if (!value || !value.length) return '';
  return value
    .split('_')
    .map((word) =>
      word.length ? word[0] + word.substring(1).toLowerCase() : '',
    )
    .join(' ');
};

/**
 * Function that works with SearchBox and Strictly for member search to hide suggestions popup
 *
 * @param {*} searchResults list of member from member search API
 * @param {*} inputValue User input string
 * @returns
 */
export const filterMemberOptionsWithoutHint = (
  searchResults,
  { inputValue = '' },
  seeAllResults,
) => {
  if (inputValue.length) {
    const filteredResults = searchResults.filter(
      (option) =>
        option.key.toLowerCase().indexOf(inputValue.trim().toLowerCase()) !==
        -1,
    );
    if (filteredResults.length > 10 && !seeAllResults) {
      return [...filteredResults.slice(0, 10), { more: true }];
    }
    return filteredResults;
  }
  return []; // Hide options on initial search state.
};

export const roundMileage = (mileage = 0, mileageRoundPreference = 'none') => {
  const toTrunc = (x, n) => {
    const v = (typeof x === 'string' ? x : x.toString()).split('.');

    if (n <= 0) {
      return v[0];
    }

    let f = v[1] || '';

    if (f.length > n) {
      return `${v[0]}.${f.substr(0, n)}`;
    }

    while (f.length < n) {
      f += '0';
    }

    return `${v[0]}.${f}`;
  };
  switch (mileageRoundPreference) {
    case ROUND_MILES_VALUES.CEILING:
      return Math.ceil(mileage);
    case ROUND_MILES_VALUES.FLOOR:
      return Math.floor(mileage);
    case ROUND_MILES_VALUES.NONE:
      return Number(toTrunc(mileage, 2));
    default:
      return Number(toTrunc(mileage, 2));
  }
};

export const sortArrayList = (list, key) => {
  if (key) {
    return list.sort((a, b) => (a[key] > b[key] ? 1 : -1));
  }
  return list.sort((a, b) => (a > b ? 1 : -1));
};

export const sortStringArrayList = (list, index) => {
  if (index) {
    return list.sort((a, b) => a[index].localeCompare(b[index]));
  }
  return list.sort((a, b) => a.localeCompare(b));
};

export const getAddressComponentFromGeoCode = (addressComponents) => {
  const [
    streetNumberComponent,
    streetNameComponent,
    neighborhoodComponent,
    cityComponent,
    countyComponent,
    stateComponent,
    zipCodeComponent,
    zipExtensionComponent,
    countryComponent,
  ] = [
    addressComponents.find((component) =>
      component.types.includes('street_number'),
    ),
    addressComponents.find((component) => component.types.includes('route')),
    addressComponents.find((component) =>
      component.types.includes('neighborhood'),
    ),
    addressComponents.find(
      (component) =>
        component.types.includes('locality') ||
        component.types.includes('sublocality'),
    ),
    addressComponents.find((component) =>
      component.types.includes('administrative_area_level_2'),
    ),
    addressComponents.find((component) =>
      component.types.includes('administrative_area_level_1'),
    ),
    addressComponents.find((component) =>
      component.types.includes('postal_code'),
    ),
    addressComponents.find((component) =>
      component.types.includes('postal_code_suffix'),
    ),
    addressComponents.find((component) => component.types.includes('country')),
  ];
  return {
    addressOne: `${streetNumberComponent?.long_name || ''} ${
      streetNameComponent?.long_name || ''
    }`,
    neighborhood: neighborhoodComponent?.long_name || '',
    city: cityComponent?.long_name || '',
    county: countyComponent?.long_name || '',
    state: stateComponent?.short_name || '',
    zip: zipCodeComponent?.long_name?.slice(0, 5) || '', // trim long zipcodes
    zipExtension: zipExtensionComponent?.long_name || '',
    country: countryComponent?.long_name || '',
  };
};

export const hasAnyField = (obj = {}, requiredFields = []) =>
  requiredFields.some((field) => !!obj[field]);

export const generateUpdateBodyFromFormik = (touched, values) => {
  const updateBody = {};

  const handleObject = (obj, touchObj, updateObj) => {
    // eslint-disable-next-line no-restricted-syntax,no-unused-vars
    for (const key in touchObj) {
      if (typeof touchObj[key] === 'boolean' && touchObj[key]) {
        // eslint-disable-next-line no-param-reassign
        updateObj[key] = obj[key];
      } else if (Array.isArray(touchObj[key])) {
        // eslint-disable-next-line no-param-reassign
        updateObj[key] = [];

        touchObj[key].forEach((arrayItem, index) => {
          // For array items, include all properties from the values object
          // when any property is touched. This is necessary for fields like
          // customFields where the backend needs both the identifier (fieldName)
          // and the value (fieldValue) to process the update correctly.
          // eslint-disable-next-line no-param-reassign
          updateObj[key].push({ ...obj[key][index] });
        });
      } else if (typeof touchObj[key] === 'object') {
        // eslint-disable-next-line no-param-reassign
        updateObj[key] = {};
        handleObject(obj[key], touchObj[key], updateObj[key]);
      }
    }
  };

  handleObject(values, touched, updateBody);

  return updateBody;
};

export const getDateTimeKey = (tripType, bookingType) => {
  switch (tripType) {
    case TRIP_TYPE_CODES.SCHEDULED:
      return bookingType === BOOKING_TYPE_VALUES.APPOINTMENT
        ? 'dropOffDateTime'
        : 'pickupDateTime';
    case TRIP_TYPE_CODES.MILEAGE_REIMBURSEMENT:
      return 'pickupDateTime';
    case TRIP_TYPE_CODES.ANCILLARY_SERVICES:
    case TRIP_TYPE_CODES.PUBLIC_TRANSPORTATION:
      return 'dropOffDateTime';
    default:
      return 'pickupDateTime';
  }
};

export const getTimeKey = (tripType, bookingType) => {
  switch (tripType) {
    case TRIP_TYPE_CODES.SCHEDULED:
      return bookingType === BOOKING_TYPE_VALUES.APPOINTMENT
        ? 'dropOffTime'
        : 'pickupTime';
    case TRIP_TYPE_CODES.MILEAGE_REIMBURSEMENT:
      return 'pickupTime';
    case TRIP_TYPE_CODES.PUBLIC_TRANSPORTATION:
      return 'dropOffTime';
    default:
      return 'pickupTime';
  }
};

export const convertSecondsToMin = (field) => {
  const duration = Duration.fromObject({
    seconds: field || 0,
  });
  let time;
  if (field) {
    if (parseInt(duration.toFormat('mm'), 10) > 59) {
      time = duration.toFormat("hh 'hr' mm 'mins'");
    } else {
      time = duration.toFormat("mm 'mins'");
    }
  } else {
    time = '';
  }
  return time;
};

export const reimbursableDriverFormValuesToRequestBody = (
  values,
  memberUuid,
) => {
  const { driver, payableToAddress } = values;

  const associatedPassengers = [
    {
      uuid: memberUuid,
      entity: 'patient',
      relationship: driver.relationToPassenger,
    },
  ];

  return {
    ...values,
    driver: {
      ...driver,
      relationToPassenger: undefined,
    },
    payableToAddress,
    associatedPassengers,
  };
};

export const getCustomParamsFromMemberSearchTerm = (searchTerm) => {
  const searchTermTrimmed = searchTerm.trim();
  if (!searchTermTrimmed.length) return null;
  const searchTermPrefix = searchTermTrimmed[0];
  const searchTermValue = searchTermTrimmed.substring(1);
  if (!['!', '@', '#'].includes(searchTermPrefix)) return null;
  switch (searchTermPrefix) {
    case '!':
      if (!DateTime.fromFormat(searchTermValue, 'MM/dd/yyyy').isValid)
        return null;
      return {
        dateOfBirth: DateTime.fromFormat(searchTermValue, 'MM/dd/yyyy', {
          zone: 'utc',
        })
          .startOf('day')
          .toISO(),
      };
    case '@':
      return { name: searchTermValue };
    case '#':
      return { memberId: searchTermValue };
    default:
      return null;
  }
};

/**
 * Get the great-circle distance between two points on a sphere given their longitudes and latitudes.
 *
 * @param {Number[]} originLongLat - The longitude and latitude of the origin point.
 * @param {Number[]} destLongLat - The longitude and latitude of the destination point.
 *
 * @returns {Number | null} The distance between the two points in miles. Returns null if any of the coordinates are missing.
 */
export const getHaversineMileage = (
  [originLong, originLat],
  [destLong, destLat],
) => {
  if (!originLong || !originLat || !destLong || !destLat) {
    return null;
  }

  // Convert degrees to radians
  const [originLongRad, originLatRad, destLongRad, destLatRad] = [
    originLong,
    originLat,
    destLong,
    destLat,
  ].map((coord) => coord * (Math.PI / 180));

  // https://en.wikipedia.org/wiki/Haversine_formula#Formulation
  const haversineDistance =
    2 *
    MEAN_EARTH_RADIUS *
    Math.asin(
      Math.sqrt(
        (1 -
          Math.cos(destLatRad - originLatRad) +
          Math.cos(originLatRad) *
            Math.cos(destLatRad) *
            (1 - Math.cos(destLongRad - originLongRad))) /
          2,
      ),
    );

  return haversineDistance;
};

/**
 * Get the approximate travel time for a given distance and average speed.
 *
 * @param {Number} mileage - The distance to travel in miles.
 * @param {Number} averageMilesPerHour - The average speed in miles per hour.
 *
 * @returns {Object} An object containing the approximate travel time in the largest units (years, quarters, months, weeks, days, hours, minutes, seconds, milliseconds).
 */
export const getApproximateTravelTime = (mileage, averageMilesPerHour = 40) => {
  const travelTimeInHours = mileage / averageMilesPerHour;
  const travelTimeInSeconds = travelTimeInHours * SECONDS_PER_HOUR;

  return Duration.fromObject({
    seconds: travelTimeInSeconds,
  })
    .rescale()
    .toObject();
};

/**
 * Get the approximate travel time between two locations given their longitudes and latitudes.
 *
 * @param {Number[]} originLongLat  - The longitude and latitude of the origin point.
 * @param {Number[]} destLongLat - The longitude and latitude of the destination point.
 * @param {Number} averageMilesPerHour - The average speed in miles per hour.
 *
 * @returns {Object} An object containing the approximate travel time in the largest units (years, quarters, months, weeks, days, hours, minutes, seconds, milliseconds).
 */
export const getApproximateTravelTimeBetweenLocations = (
  originLongLat,
  destLongLat,
  averageMilesPerHour = 40,
) => {
  const mileage = getHaversineMileage(originLongLat, destLongLat);
  return getApproximateTravelTime(mileage, averageMilesPerHour);
};

/**
 * Format location options for select component
 * @param locations {Object[]} normal search results
 * @param approvedLocations {Object[]} - approved locations
 * @return {Object[]}
 */
export const formatLocationsOptions = (locations, approvedLocations) => {
  if (approvedLocations?.length) {
    return approvedLocations.map((location) => ({
      placeID: location?.location?.googlePlaceId,
      addressText: location?.location?.addressText,
      data: {
        ...location,
        placeID: location?.location?.googlePlaceId,
        locationCategory:
          location?.locationCategory ?? LOCATION_CATEGORIES.APPROVED,
      },
    }));
  }

  return locations.map((location) => ({
    placeID: location.placeID,
    addressText: location.formattedAddress,
    data: {
      ...location,
      locationCategory: location?.locationCategory ?? LOCATION_CATEGORIES.ALL,
    },
  }));
};

/**
 *
 * @param approvedLocations {Object[]} - approved locations
 * @return {Object[]}
 */
const formatApprovedLocations = (approvedLocations) =>
  approvedLocations.map((location) => ({
    placeID: location?.location?.googlePlaceId,
    addressText: location?.location?.addressText,
    data: {
      ...location,
      placeId: location?.googlePlaceId,
      locationCategory:
        location?.locationCategory ?? LOCATION_CATEGORIES.APPROVED,
    },
  }));

/**
 * Formats an array of suggested locations by adding a location category.
 * If the array is empty, returns a placeholder object indicating the category is empty.
 *
 * @param {Object[]} locationsArray - The array of location objects to format.
 * @param {string} categoryName - The name of the location category to assign.
 * @returns {Object[]} The formatted array of location objects, or a placeholder if empty.
 */
export const formatSuggestedLocations = (locationsArray, categoryName) => {
  if (!locationsArray) return [];

  if (locationsArray?.length === 0) {
    return [
      {
        isEmpty: true,
        locationCategory: categoryName,
        uuid:
          Math.floor((1 + Math.random()) * 0x10000000).toString(16) +
          categoryName,
      },
    ];
  }

  return locationsArray?.map((location) => ({
    ...location,
    locationCategory: `${categoryName}`,
  }));
};

/**
 *
 * @param searchResults {Object[]} - searched locations
 * @param locationSearchInput {String} - location inputs
 * @param suggestedLocations {Object[]} - suggested approved locations
 * @return {*}
 */
export const filterLocationOptions = (
  searchResults,
  locationSearchInput,
  suggestedLocations,
) => {
  const safeSuggestedLocation = Array.isArray(suggestedLocations)
    ? suggestedLocations
    : [];

  const hasRealSuggestedLocation = safeSuggestedLocation.some(
    (loc) => !loc?.isEmpty,
  );

  // No search → show suggested groups (even placeholders)
  if (!locationSearchInput || hasRealSuggestedLocation) {
    return formatApprovedLocations(safeSuggestedLocation);
  }

  // Otherwise show search results
  return searchResults;
};

const changeTimezone = (luxonTime) =>
  new Date(
    Date.UTC(
      luxonTime.get('year'),
      luxonTime.get('month') - 1,
      luxonTime.get('day'),
      luxonTime.get('hour'),
      luxonTime.get('minute'),
      0,
      0,
    ),
  );

export const createRuleSet = (
  frequency,
  interval,
  byWeekday,
  endCount,
  startDate,
  until,
  timezone = 'America/New_York',
  // eslint-disable-next-line no-unused-vars
  rruleAdditionalOptions = {},
  exclusionDates = [],
  bymonthday = null,
  monthlyWeekday = null,
  bysetpos = null,
) => {
  const rruleSet = new RRuleSet();
  const rrule = new RRule({
    tzid: timezone,
    freq: RRule[FREQUENCY_KEY_MAP[frequency]],
    interval,
    byweekday: byWeekday?.map((each) => RRule[each]) ?? [],
    dtstart: changeTimezone(startDate),
    ...(until && { until: changeTimezone(until) }),
    ...(endCount && { count: endCount }),
    ...(bymonthday && { bymonthday }),
    ...(monthlyWeekday && { byweekday: RRule[monthlyWeekday] }),
    ...(bysetpos && { bysetpos }),
  });
  rruleSet.rrule(rrule);

  if (exclusionDates?.length) {
    exclusionDates.forEach((exclusionDate) => {
      rruleSet.exdate(changeTimezone(exclusionDate));
    });
  }
  return rruleSet;
};

/**
 * Get a simple array of all column definitions when the grid contains column groups.
 *
 * @param {array} columns - The columnDefs of ag-grid with column groups.
 * @returns {array} - Returns a simple array of all columnDefs.
 */
export const getFlattenedGridColumns = (columns) =>
  columns?.reduce((acc, curr) => acc.concat(curr.children), []);

export const agGridAutoSizeStrategy = {
  type: 'fitCellContents',
};

export const getTodaysRange = () => {
  const startDate = DateTime.now().startOf('day').toUTC().toISO();
  const endDate = DateTime.now().endOf('day').toUTC().toISO();

  return [startDate, endDate];
};

export const isPickupDateToday = (trip) => {
  const {
    request: { pickupDateTime },
  } = trip;
  const todaysDateRange = getTodaysRange();
  return (
    todaysDateRange[0] <= pickupDateTime && pickupDateTime <= todaysDateRange[1]
  );
};

export const filterKinetikUsers = (currentUser, users) => {
  if (currentUser?.email?.includes('@kinetik.care')) {
    return users || [];
  }

  return users.filter((user) => !user.email.includes('@kinetik.care')) || [];
};

export const findEstimateByRideType = (allEstimates, type, tncId) => {
  const estimates = allEstimates[tncId] || [];
  switch (tncId) {
    case TNC_PROVIDERS.UBER:
      return estimates.filter((est) => est.product.display_name === type);
    case TNC_PROVIDERS.LYFT:
      return estimates.filter((est) => est.display_name === type);
    default:
      return null;
  }
};

export const mapObjectForUberLyftAssign = (tncId, estimateObj) => {
  switch (tncId) {
    case TNC_PROVIDERS.UBER:
      return {
        bookingDetails: {
          source: TNC_PROVIDERS.UBER,
          productId: estimateObj.product.product_id,
          displayName: estimateObj.product.display_name,
        },
        estimate: {
          fare: {
            source: TNC_PROVIDERS.UBER,
            fareInCents:
              estimateObj.estimate_info.fare?.value ||
              estimateObj.estimate_info?.estimate?.low_estimate ||
              0,
            fareId: estimateObj.estimate_info.fare_id,
            fareExpiresAt: new Date(
              (estimateObj.estimate_info?.fare?.expires_at || 0) * 1000,
            ), // Cost token expires in 60 seconds from recieving
            computedAt: new Date(),
          },
          route: {
            source: TNC_PROVIDERS.UBER,
            mileage: estimateObj.estimate_info.trip?.distance_estimate || 0,
            durationInSeconds:
              estimateObj.estimate_info.trip?.duration_estimate || 0,
          },
        },
      };
    case TNC_PROVIDERS.LYFT:
      return {
        bookingDetails: {
          source: TNC_PROVIDERS.LYFT,
          productId: estimateObj.ride_type,
          displayName: estimateObj.display_name,
        },
        estimate: {
          fare: {
            source: TNC_PROVIDERS.LYFT,
            fareInCents:
              estimateObj.estimated_cost_cents_min ||
              estimateObj.estimated_cost_cents_max ||
              0,
            fareId: estimateObj.cost_token,
            fareExpiresAt: new Date(new Date().getTime() + 60000), // Cost token expires in 60 seconds from recieving
            computedAt: new Date(),
          },
          route: {
            source: TNC_PROVIDERS.LYFT,
            mileage: estimateObj.estimated_distance_miles,
            durationInSeconds: estimateObj.estimated_duration_seconds,
          },
        },
      };
    default:
      return {};
  }
};

const hasAutoAssignmentExpired = (trip) => {
  if (!trip) return false;
  const { __autoAssignment = null } = trip;
  if (!__autoAssignment) return false;

  const expiresAt = DateTime.fromISO(
    __autoAssignment.manualAssignmentVisibilityTimeoutExpiresAt,
  );
  const currentDate = DateTime.now();

  return expiresAt < currentDate;
};

export const isReAssignable = (trip) => {
  if (!trip) return false;
  return (
    VALID_RE_ASSIGNABLE_STATUS_LIST.includes(trip.status) &&
    hasAutoAssignmentExpired(trip)
  );
};
